<template>
  <div>
    <BusinessBUserHeader :style="{ backgroundColor: 'var(--tc-color-bg-list)' }"></BusinessBUserHeader>
    <BaseAZError v-if="isError" :title="errorMessage"></BaseAZError>
    <div v-else class="experience-container">
      <van-swipe
        lazy-render
        class="detail-swipe h-[14.5rem]"
        :autoplay="3000"
        indicator-color="white"
      >
        <van-swipe-item v-for="(image, imageIndex) in [experienceInfo?.cover, ...experienceInfo?.images?.map(item => item.imageUrl!) || []]" :key="image">
          <van-image height="100%" width="100%" :src="image" fit="cover"
          :alt="`${experienceInfo?.specialTopicName || 'Experience Image'} - Image ${imageIndex + 1}`"
          :title="experienceInfo?.specialTopicName || 'Experience Slide'"
          :aria-label="`${experienceInfo?.specialTopicName || 'Experience'} - Image ${imageIndex + 1}`"
          role="img"
       />
        </van-swipe-item>
      </van-swipe>
      <div class="experience-info">
        <h1 class="mb-2.5 text-5.5">
          {{ experienceInfo?.specialTopicName }}
        </h1>
        <van-text-ellipsis
          rows="3"
          :content="experienceInfo?.intro"
          :expand-text="t('key.common.expand')"
          :collapse-text="t('key.common.collapse')"
        >
          <template #action="{ expanded }">
            <div inline-flex="~ items-center">
              <span class="mr-1 font-400 underline" text="xs black" font="500">
                {{ expanded ? t('key.experience.read.less') : t('key.experience.read.more') }}
              </span>
              <img
                src="~assets/svg/arrow-down.svg"
                :width="10"
                :class="expanded ? 'rotate-180 mt-1' : 'mt-1'"
              />
            </div>
          </template>
        </van-text-ellipsis>
      </div>
      <van-divider />
      <div class="mb-2 flex px-4">
        <SelectDate class="mr-2" :attr-name="experienceInfo?.specialTopicName ?? ''">
        </SelectDate>
        <Filter />
      </div>
      <div v-for="(supplier, supplierIndex) in supplierList" :key="supplier.id" class="supplier-item">
        <div class="supplier-info">
          <div class="supplier-left">
            <div>
              <img :src="supplier.logo" class="supplier-logo" />
              <div
                :style="{ visibility: !supplier.showAll && !supplier.showMore ? 'visible' : 'hidden' }"
                class="show-more" color="#888888" text="2.5" @click="showMoreHandler(supplier)"
              >
                <ElIcon><ElIconArrowDown></ElIconArrowDown></ElIcon>
                <van-highlight
                  decoration="underline"
                  :keywords="t('key.attractions.num.more', [supplier.details.length > defaultExpandLength ? defaultExpandLength : supplier.details.length])"
                  :source-string="t('key.attraction.detail.price.moreOptions', [t('key.attractions.num.more', [supplier.details.length > defaultExpandLength ? defaultExpandLength : supplier.details.length])])"
                  highlight-class="font-700"
                />
              </div>
            </div>
            <div class="supplier-middle">
              <div class="text-base text-[#868686] font-800 leading-tight">
                {{ t('key.attraction.detail.price.from') }}
              </div>
              <div class="supplier-price">
                {{ getPrice(supplier.price) }}
              </div>
              <div v-if="experienceInfo?.cashbackDesc" text-2.5 :style="{ color: 'var(--tc-color-cashback-desc)' }">
                {{ experienceInfo?.cashbackDesc }}
              </div>
            </div>
          </div>
          <van-button
            color="var(--tc-color-view-button)"
            size="small"
            @click="clickViewDeal(supplier.details[0], supplierIndex, 0, true)"
          >
            <div class="flex items-center text-xs">
              {{ getPrice(supplier.price) ? t('key.attraction.detail.price.viewDeal') : t("key.attraction.detail.price.getPrice") }}
              <NuxtIcon name="my-icon:arrow-right" class="ml-1 w-4px" />
            </div>
          </van-button>
        </div>
        <div v-show="supplier.showAll || supplier.showMore" class="ticket-list">
          <div v-for="(item, productIndex) in supplier.showAll ? supplier.details : supplier.details.slice(0, defaultExpandLength)" :key="item.attSourcePkId" class="ticket-item">
            <div class="sku-left">
              <div class="sku-title">
                <span>{{ item.productTitle }}</span>
                <van-icon v-if="item.productDescription" class="info-icon" name="info-o" cursor-pointer @click="showSkuDesc(item)" />
              </div>
              <div class="sku-price">
                {{ getPrice(item.price) }}
              </div>
            </div>
            <div class="flex cursor-pointer select-none items-center justify-end" :style="{ color: 'var(--tc-color-view-button)' }" @click="clickViewDeal(item, supplierIndex, productIndex, false)">
              <span whitespace-nowrap>{{ t('key.attraction.detail.price.viewDeal') }}</span>
              <ElIcon><ElIconArrowRight></ElIconArrowRight></ElIcon>
            </div>
          </div>
          <div class="inline-flex cursor-pointer select-none items-center gap-1 text-2.5 color-#888888" @click="showLessOrShowAll(supplier)">
            <template v-if="!supplier.showAll">
              <ElIcon><ElIconArrowDown></ElIconArrowDown></ElIcon>
              <span>{{ t('key.attraction.detail.price.allOptions') }}</span>
            </template>
            <template v-else>
              <ElIcon><ElIconArrowUp></ElIconArrowUp></ElIcon>
              <span>{{ t('key.attraction.detail.price.lessOptions') }}</span>
            </template>
          </div>
        </div>
      </div>
      <Empty v-if="!supplierList.length" :show-btn="false" desc=""></Empty>
    </div>
    <van-popup
      v-model:show="skuDescVisible"
      position="bottom"
      closeable
      round
      :style="{ maxHeight: '85%' }"
      close-icon="close"
    >
      <div class="popup-content" flex="~ col">
        <h2>{{ t('key.experience.detail.sku.desc') }}</h2>
        <div flex="1" overflow-y="auto" class="experience-desc" v-html="currentSku?.productDescription"></div>
      </div>
    </van-popup>
  </div>
</template>

<script lang="ts" setup>
import SelectDate from '../components/SelectDate.vue'
import Filter from '../components/Filter.vue'
import type { AttSpecialTopicConfigGroupDetailResponseDTO } from '~/apis/back/types'

const {
  experienceInfo,
  supplierList,
  isError,
  errorMessage,
  defaultExpandLength,
  currentSku,
  skuDescVisible,
  viewDeal,
  showLessOrShowAll,
  showMoreHandler,
  getPrice,
  showSkuDesc,
} = useExperienceDetail()

const { t } = useI18n()

function clickViewDeal(item: AttSpecialTopicConfigGroupDetailResponseDTO, supplierIndex: number, productIndex: number, isBigBtn: boolean) {
  showLoadingToast({ forbidClick: true })
  viewDeal(item, supplierIndex, productIndex, isBigBtn)
}
</script>

<style lang="scss" scoped>
.experience-container {
  padding-bottom: 20px;
  width: 100%;
  max-width: 1240px;
  .experience-info {
    position: relative;
    margin-top: -12px;
    padding: 12px 16px 0 16px;
    z-index: 1;
    background-color: #fff;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
}
.supplier-item {
  padding: 12px 16px;
  border-bottom: 1px solid #D9D9D9;

  .supplier-logo {
    width: 72px;
    margin-bottom: 8px;
    object-fit: contain;
  }
}

.supplier-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 25px;
  .supplier-left {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: space-between;
  }
  .supplier-middle {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .supplier-price {
    font-size: 16px;
    font-weight: 500;
  }
  .show-more {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
  }
}

.ticket-list {
  margin-top: 10px;
  .ticket-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 40px;
    margin-bottom: 4px;
    .sku-left {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 10px;
      justify-content: space-between;
    }
    .sku-title {
      flex: 1;
      >span {
        flex: 1;
        font-size: 12px;
      }
      .info-icon {
        margin-left: 4px;
      }
    }
    .sku-price {
      font-weight: 500;
      font-size: 12px;
    }
  }
}
.popup-content {
  height: 100%;
  padding: 16px;
  h2 {
    margin-bottom: 10px;
  }
}
</style>

<style>
:root:root {
  --van-highlight-tag-color: #888888;
  --van-swipe-indicator-margin:24px;
}

.experience-desc img {
  max-width: 100%;
}
</style>
